// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - ACHIEVEMENTS MANAGER
// Tracks user achievements, milestones, and badges
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// ACHIEVEMENT DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════

export const ACHIEVEMENTS = {
  // First Steps
  FIRST_PURIFICATION: {
    id: 'first_purification',
    name: 'First Steps',
    description: 'Complete your first purification',
    icon: '🌱',
    category: 'Getting Started',
    requirement: { type: 'purifications', value: 1 },
    rarity: 'common',
  },
  
  // Volume Milestones
  PURIFIER_10: {
    id: 'purifier_10',
    name: 'Getting Started',
    description: 'Complete 10 purifications',
    icon: '⚡',
    category: 'Volume',
    requirement: { type: 'purifications', value: 10 },
    rarity: 'common',
  },
  PURIFIER_50: {
    id: 'purifier_50',
    name: 'Dedicated Cleaner',
    description: 'Complete 50 purifications',
    icon: '🔥',
    category: 'Volume',
    requirement: { type: 'purifications', value: 50 },
    rarity: 'rare',
  },
  PURIFIER_100: {
    id: 'purifier_100',
    name: 'Cleanup Veteran',
    description: 'Complete 100 purifications',
    icon: '💎',
    category: 'Volume',
    requirement: { type: 'purifications', value: 100 },
    rarity: 'epic',
  },
  PURIFIER_500: {
    id: 'purifier_500',
    name: 'Master Purifier',
    description: 'Complete 500 purifications',
    icon: '👑',
    category: 'Volume',
    requirement: { type: 'purifications', value: 500 },
    rarity: 'legendary',
  },
  
  // Purity Milestones
  PURITY_100: {
    id: 'purity_100',
    name: 'Purity Seeker',
    description: 'Earn 100 purity points',
    icon: '✨',
    category: 'Purity',
    requirement: { type: 'purity', value: 100 },
    rarity: 'common',
  },
  PURITY_1000: {
    id: 'purity_1000',
    name: 'Purity Collector',
    description: 'Earn 1,000 purity points',
    icon: '🌟',
    category: 'Purity',
    requirement: { type: 'purity', value: 1000 },
    rarity: 'rare',
  },
  PURITY_10000: {
    id: 'purity_10000',
    name: 'Purity Master',
    description: 'Earn 10,000 purity points',
    icon: '💫',
    category: 'Purity',
    requirement: { type: 'purity', value: 10000 },
    rarity: 'epic',
  },
  PURITY_100000: {
    id: 'purity_100000',
    name: 'Purity Legend',
    description: 'Earn 100,000 purity points',
    icon: '🏆',
    category: 'Purity',
    requirement: { type: 'purity', value: 100000 },
    rarity: 'legendary',
  },
  
  // Streak Achievements
  STREAK_3: {
    id: 'streak_3',
    name: 'Consistency Starts',
    description: 'Maintain a 3-day streak',
    icon: '🔥',
    category: 'Streaks',
    requirement: { type: 'streak', value: 3 },
    rarity: 'common',
  },
  STREAK_7: {
    id: 'streak_7',
    name: 'Week Warrior',
    description: 'Maintain a 7-day streak',
    icon: '⚡',
    category: 'Streaks',
    requirement: { type: 'streak', value: 7 },
    rarity: 'rare',
  },
  STREAK_30: {
    id: 'streak_30',
    name: 'Monthly Champion',
    description: 'Maintain a 30-day streak',
    icon: '👑',
    category: 'Streaks',
    requirement: { type: 'streak', value: 30 },
    rarity: 'epic',
  },
  STREAK_100: {
    id: 'streak_100',
    name: 'Unstoppable',
    description: 'Maintain a 100-day streak',
    icon: '💎',
    category: 'Streaks',
    requirement: { type: 'streak', value: 100 },
    rarity: 'legendary',
  },
  
  // Efficiency Achievements
  EFFICIENT_PURIFIER: {
    id: 'efficient_purifier',
    name: 'Efficient Purifier',
    description: 'Average 10+ objects per purification',
    icon: '📈',
    category: 'Efficiency',
    requirement: { type: 'efficiency', value: 10 },
    rarity: 'rare',
  },
  BULK_CLEANER: {
    id: 'bulk_cleaner',
    name: 'Bulk Cleaner',
    description: 'Purify 50+ objects in one transaction',
    icon: '📦',
    category: 'Efficiency',
    requirement: { type: 'single_purification_objects', value: 50 },
    rarity: 'epic',
  },
  
  // Object Count Achievements
  OBJECTS_100: {
    id: 'objects_100',
    name: 'Cleanup Crew',
    description: 'Clean 100 total objects',
    icon: '🗑️',
    category: 'Objects',
    requirement: { type: 'total_objects', value: 100 },
    rarity: 'common',
  },
  OBJECTS_1000: {
    id: 'objects_1000',
    name: 'Sanitation Expert',
    description: 'Clean 1,000 total objects',
    icon: '🧹',
    category: 'Objects',
    requirement: { type: 'total_objects', value: 1000 },
    rarity: 'rare',
  },
  OBJECTS_10000: {
    id: 'objects_10000',
    name: 'Cleanup Legend',
    description: 'Clean 10,000 total objects',
    icon: '🏆',
    category: 'Objects',
    requirement: { type: 'total_objects', value: 10000 },
    rarity: 'legendary',
  },
  
  // Tier Achievements
  TIER_APPRENTICE: {
    id: 'tier_apprentice',
    name: 'Apprentice Achieved',
    description: 'Reach Apprentice tier',
    icon: '🎓',
    category: 'Tiers',
    requirement: { type: 'tier', value: 1 },
    rarity: 'common',
  },
  TIER_ADEPT: {
    id: 'tier_adept',
    name: 'Adept Achieved',
    description: 'Reach Adept tier',
    icon: '⭐',
    category: 'Tiers',
    requirement: { type: 'tier', value: 2 },
    rarity: 'common',
  },
  TIER_EXPERT: {
    id: 'tier_expert',
    name: 'Expert Achieved',
    description: 'Reach Expert tier',
    icon: '💎',
    category: 'Tiers',
    requirement: { type: 'tier', value: 3 },
    rarity: 'rare',
  },
  TIER_MASTER: {
    id: 'tier_master',
    name: 'Master Achieved',
    description: 'Reach Master tier',
    icon: '👑',
    category: 'Tiers',
    requirement: { type: 'tier', value: 4 },
    rarity: 'epic',
  },
  TIER_GRANDMASTER: {
    id: 'tier_grandmaster',
    name: 'Grandmaster Achieved',
    description: 'Reach Grandmaster tier',
    icon: '🔱',
    category: 'Tiers',
    requirement: { type: 'tier', value: 5 },
    rarity: 'epic',
  },
  TIER_SOVEREIGN: {
    id: 'tier_sovereign',
    name: 'Sovereign Achieved',
    description: 'Reach Sovereign tier',
    icon: '⚜️',
    category: 'Tiers',
    requirement: { type: 'tier', value: 6 },
    rarity: 'legendary',
  },
  
  // Category Specialists
  TOXIC_HUNTER: {
    id: 'toxic_hunter',
    name: 'Toxic Hunter',
    description: 'Clean 100 toxic objects',
    icon: '🔴',
    category: 'Specialists',
    requirement: { type: 'category_toxic', value: 100 },
    rarity: 'rare',
  },
  SPAM_DESTROYER: {
    id: 'spam_destroyer',
    name: 'Spam Destroyer',
    description: 'Clean 100 spam objects',
    icon: '🟡',
    category: 'Specialists',
    requirement: { type: 'category_spam', value: 100 },
    rarity: 'rare',
  },
  DUST_COLLECTOR: {
    id: 'dust_collector',
    name: 'Dust Collector',
    description: 'Clean 100 dust objects',
    icon: '⚪',
    category: 'Specialists',
    requirement: { type: 'category_dust', value: 100 },
    rarity: 'rare',
  },
  
  // Special Achievements
  EARLY_ADOPTER: {
    id: 'early_adopter',
    name: 'Early Adopter',
    description: 'One of the first 1000 users',
    icon: '🚀',
    category: 'Special',
    requirement: { type: 'special', value: 'early_adopter' },
    rarity: 'legendary',
  },
  TOILET_ENTHUSIAST: {
    id: 'toilet_enthusiast',
    name: 'Toilet Enthusiast',
    description: 'Complete 10 flushes in Toilet Mode',
    icon: '🚽',
    category: 'Special',
    requirement: { type: 'toilet_flushes', value: 10 },
    rarity: 'epic',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// ACHIEVEMENT TRACKING
// Supabase = source of truth; localStorage = fast cache
// ═══════════════════════════════════════════════════════════════════════════

import { saveAchievementsToSupabase, getAchievementsFromSupabase } from './supabaseManager';

const STORAGE_KEY = 'distil_achievements';

function getStorageKey(walletAddress) {
  if (!walletAddress) return `${STORAGE_KEY}_demo`;
  return `${STORAGE_KEY}_${walletAddress}`;
}

// Get unlocked achievements from localStorage
export function getUnlockedAchievements(walletAddress = null) {
  try {
    const stored = localStorage.getItem(getStorageKey(walletAddress));
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading achievements:', error);
    return [];
  }
}

// Save unlocked achievements to localStorage
function saveUnlockedAchievements(achievements, walletAddress = null) {
  try {
    localStorage.setItem(getStorageKey(walletAddress), JSON.stringify(achievements));
  } catch (error) {
    console.error('Error saving achievements:', error);
  }
}

/**
 * Restore achievements from Supabase into localStorage (call on wallet connect)
 */
export async function syncAchievementsFromSupabase(walletAddress) {
  if (!walletAddress) return null;
  try {
    const remote = await getAchievementsFromSupabase(walletAddress);
    if (!remote || remote.length === 0) return null;

    const local = getUnlockedAchievements(walletAddress);
    // Merge: keep any IDs from either source
    const merged = [...new Set([...local, ...remote])];
    saveUnlockedAchievements(merged, walletAddress);
    console.log('✅ Achievements restored from Supabase:', merged.length, 'unlocked');
    return merged;
  } catch (err) {
    console.error('syncAchievementsFromSupabase failed:', err);
    return null;
  }
}

// Unlock an achievement — saves to localStorage + Supabase
export function unlockAchievement(achievementId, walletAddress = null) {
  const unlocked = getUnlockedAchievements(walletAddress);

  if (!unlocked.includes(achievementId)) {
    unlocked.push(achievementId);
    saveUnlockedAchievements(unlocked, walletAddress);

    // Sync to Supabase in background (non-blocking)
    if (walletAddress) {
      saveAchievementsToSupabase(walletAddress, unlocked).catch(err =>
        console.error('Achievement Supabase sync failed (non-blocking):', err)
      );
    }
    return true; // Newly unlocked
  }

  return false; // Already unlocked
}

// Check all achievements against current stats
export function checkAchievements(stats, walletAddress = null) {
  const {
    totalPurifications = 0,
    totalPurityEarned = 0,
    currentStreak = 0,
    longestStreak = 0,
    totalObjectsCleaned = 0,
    currentTier = 0,
    efficiency = 0,
    categoryBreakdown = {},
    isToiletMode = false,
    toiletFlushes = 0,
  } = stats;
  
  const newlyUnlocked = [];
  
  Object.values(ACHIEVEMENTS).forEach(achievement => {
    const { requirement, id } = achievement;
    let isEarned = false;
    
    switch (requirement.type) {
      case 'purifications':
        isEarned = totalPurifications >= requirement.value;
        break;
      case 'purity':
        isEarned = totalPurityEarned >= requirement.value;
        break;
      case 'streak':
        isEarned = longestStreak >= requirement.value;
        break;
      case 'total_objects':
        isEarned = totalObjectsCleaned >= requirement.value;
        break;
      case 'tier':
        isEarned = currentTier >= requirement.value;
        break;
      case 'efficiency':
        isEarned = efficiency >= requirement.value;
        break;
      case 'category_toxic':
        isEarned = (categoryBreakdown.TOXIC || 0) >= requirement.value;
        break;
      case 'category_spam':
        isEarned = (categoryBreakdown.SPAM || 0) >= requirement.value;
        break;
      case 'category_dust':
        isEarned = (categoryBreakdown.DUST || 0) >= requirement.value;
        break;
      case 'toilet_flushes':
        isEarned = toiletFlushes >= requirement.value;
        break;
      default:
        isEarned = false;
    }
    
    if (isEarned && unlockAchievement(id, walletAddress)) {
      newlyUnlocked.push(achievement);
    }
  });
  
  return newlyUnlocked;
}

// Get achievement progress
export function getAchievementProgress(achievement, stats, walletAddress = null) {
  const { requirement } = achievement;
  let current = 0;
  let target = requirement.value;
  
  switch (requirement.type) {
    case 'purifications':
      current = stats.totalPurifications || 0;
      break;
    case 'purity':
      current = stats.totalPurityEarned || 0;
      break;
    case 'streak':
      current = stats.longestStreak || 0;
      break;
    case 'total_objects':
      current = stats.totalObjectsCleaned || 0;
      break;
    case 'tier':
      current = stats.currentTier || 0;
      break;
    case 'efficiency':
      current = stats.efficiency || 0;
      break;
    case 'category_toxic':
      current = (stats.categoryBreakdown?.TOXIC || 0);
      break;
    case 'category_spam':
      current = (stats.categoryBreakdown?.SPAM || 0);
      break;
    case 'category_dust':
      current = (stats.categoryBreakdown?.DUST || 0);
      break;
    case 'toilet_flushes':
      current = stats.toiletFlushes || 0;
      break;
    default:
      current = 0;
  }
  
  const percentage = Math.min(100, (current / target) * 100);
  
  return {
    current,
    target,
    percentage,
    isUnlocked: getUnlockedAchievements(walletAddress).includes(achievement.id),
  };
}

// Get achievements by category
export function getAchievementsByCategory(walletAddress = null) {
  const byCategory = {};
  
  Object.values(ACHIEVEMENTS).forEach(achievement => {
    const { category } = achievement;
    if (!byCategory[category]) {
      byCategory[category] = [];
    }
    byCategory[category].push(achievement);
  });
  
  return byCategory;
}

// Get achievement stats
export function getAchievementStats(walletAddress = null) {
  const unlocked = getUnlockedAchievements(walletAddress);
  const total = Object.keys(ACHIEVEMENTS).length;
  
  const byRarity = {
    common: 0,
    rare: 0,
    epic: 0,
    legendary: 0,
  };
  
  unlocked.forEach(id => {
    const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === id);
    if (achievement) {
      byRarity[achievement.rarity]++;
    }
  });
  
  return {
    unlocked: unlocked.length,
    total,
    percentage: Math.round((unlocked.length / total) * 100),
    byRarity,
  };
}

export default {
  ACHIEVEMENTS,
  getUnlockedAchievements,
  unlockAchievement,
  checkAchievements,
  syncAchievementsFromSupabase,
  getAchievementProgress,
  getAchievementsByCategory,
  getAchievementStats,
};
